import { SmallUppercase } from '@/components/investors/shared';
import { SeoProps } from '@/components/shared/SEO';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { Option, OptionsList, Select } from '@lemonade-hq/nuis';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { News } from './[slug]';
import { fetchWithRetry } from '../../../utils/fetchWithRetry';

type NewsSummary = Omit<News, 'content'>;

const NewsIntroItemWrapper = styled(Flex)`
  padding-block: 16px;
  border-bottom: 1px solid ${themedColor('glass')};

  &:last-child {
    border-bottom: none;
  }
`;

const ReadMore = styled(Link)`
  font-size: 1.4rem;
  font-weight: 400;
  display: inline-block;
  margin-top: 8px;
`;

const NewsContent = styled.p`
  ${font('main', { fontSize: '1.6rem', fontWeight: 400, lineHeight: '145%' })};
  text-align: left;
  text-wrap: pretty;
`;

const FilterTitle = styled.label`
  font-size: 1.6rem;
  text-align: left;
  color: ${themedColor('textPrimary')};
  font-weight: 400;
  white-space: nowrap;
`;

const NewsIntroItem: React.FC<Omit<News, 'id' | 'content'>> = ({ date, title, slug }) => {
  return (
    <NewsIntroItemWrapper flexDirection="column" gap="4px">
      <SmallUppercase>{date}</SmallUppercase>
      <NewsContent>{title}</NewsContent>
      <ReadMore href={`/investor/news/${slug}`}>Read More</ReadMore>
    </NewsIntroItemWrapper>
  );
};

const filteredNewsBySelectedYear = (news: NewsSummary[], selectedYear: number) => {
  return news.filter(({ year }) => year === selectedYear);
};

const FilteredNews = ({ news }: { news: Omit<News, 'content'>[] }) => {
  const years = useMemo(() => Array.from(new Set(news.map(news => news.year))), [news]);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useEffect(() => {
    if ((years ?? []).length === 0) return;
    setSelectedYear(Number(years[0]));
  }, [years]);

  if (selectedYear == null) return null;

  return (
    <>
      <Flex alignItems="center" gap="8px" width="100%">
        <FilterTitle>Select year</FilterTitle>
        <Box width={['100%', '160px']}>
          <Select
            value={selectedYear as unknown as string}
            onChange={value => setSelectedYear(Number(value))}
            size="medium"
          >
            <OptionsList>
              {years.map(year => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </OptionsList>
          </Select>
        </Box>
      </Flex>
      <Flex flexDirection="column">
        {filteredNewsBySelectedYear(news, selectedYear).map(({ id, ...restNews }) => {
          return <NewsIntroItem key={id} {...restNews} />;
        })}
      </Flex>
    </>
  );
};

const MAX_WIDTH = 700;

const seo = {
  title: 'Lemonade Inc. - News',
  description:
    'Stay updated with the latest news, press releases, and important announcements about Lemonade and our investor-related activities.',
} as SeoProps;

export default function NewsPage({ news }: { news: NewsSummary[] }) {
  return (
    <InvestorsPageTemplate seo={seo}>
      <Flex flexDirection="column" gap="20px" width="100%" maxWidth={MAX_WIDTH} px={[20, 20, 0]}>
        <FilteredNews news={news} />
      </Flex>
    </InvestorsPageTemplate>
  );
}

export const getNewsSummary = async (): Promise<{ data: NewsSummary[] }> => {
  try {
    return await fetchWithRetry<{ data: NewsSummary[] }>(`${process.env.NEXT_PUBLIC_INVESTORS_URL}/news-summary`);
  } catch (err) {
    console.error('feeds error', err);
    return { data: [] };
  }
};
export const getStaticProps = async () => {
  const news = await getNewsSummary();
  return {
    props: {
      news: news.data,
    },
    revalidate: 10, // 10 seconds, we need this to be wired as much as possible to the data source
  };
};
